<template>
    <div>
        <vs-popup
            title="치료실 스케줄 필터 검색"
            :active.sync="isActive"
        >
            <div class="filter-modal-container">
                <div class="filter-layout">
                    <!-- Left Sidebar: Filter Controls -->
                    <div class="filter-sidebar">
                        <div class="filter-section">
                            <h4 class="filter-section-title">치료실 선택</h4>
                            <vs-select
                                class="w-full"
                                autocomplete
                                v-model="localCriteria.medicalCd"
                                placeholder="치료실을 선택하세요"
                                @change="onFilterChange"
                            >
                                <vs-select-item :value="''" :text="'전체'" />
                                <vs-select-item
                                    v-for="(room, index) in treatmentRooms"
                                    :key="index"
                                    :value="room.MEDICAL_CD"
                                    :text="room.MEDICAL_NM"
                                />
                            </vs-select>
                        </div>

                        <div class="filter-section">
                            <h4 class="filter-section-title">날짜 선택</h4>
                            <vs-select
                                class="w-full"
                                autocomplete
                                v-model="localCriteria.date"
                                placeholder="날짜를 선택하세요"
                                @change="onFilterChange"
                            >
                                <vs-select-item :value="''" :text="'날짜 선택'" />
                                <vs-select-item
                                    v-for="(date, index) in dateList"
                                    :key="index"
                                    :value="date"
                                    :text="formatDate(date)"
                                />
                            </vs-select>
                        </div>

                        <div class="filter-section">
                            <h4 class="filter-section-title">시간 선택</h4>
                            <vs-select
                                class="w-full"
                                autocomplete
                                v-model="localCriteria.hour"
                                placeholder="시간을 선택하세요"
                                @change="onFilterChange"
                            >
                                <vs-select-item :value="''" :text="'시간 선택'" />
                                <vs-select-item :value="'ALL'" :text="'전체'" />
                                <vs-select-item
                                    v-for="hour in hourOptions"
                                    :key="hour"
                                    :value="hour"
                                    :text="`${hour}:00`"
                                />
                            </vs-select>
                        </div>

                        <div class="filter-buttons">
                            <vs-button
                                class="w-full mb-3"
                                color="primary"
                                type="filled"
                                :disabled="!isFormValid"
                                @click="handleConfirm"
                            >
                                확인
                            </vs-button>
                            <vs-button
                                class="w-full"
                                color="dark"
                                type="border"
                                @click="handleCancel"
                            >
                                취소
                            </vs-button>
                        </div>
                    </div>

                    <!-- Right Content: Result Preview -->
                    <div class="filter-content">
                        <div class="result-preview">
                            <div class="result-header">
                                <h3 class="result-title">검색 결과 미리보기</h3>
                                <div class="result-count">
                                    <span class="count-label">검색 결과:</span>
                                    <span class="count-value">{{ previewCount }}건</span>
                                </div>
                            </div>

                            <div class="result-info">
                                <template v-if="!isFormValid">
                                    <div class="info-message">
                                        <i class="feather icon-info"></i>
                                        <p>모든 필터 조건을 선택해주세요.</p>
                                    </div>
                                </template>
                                <template v-else-if="previewCount === 0">
                                    <div class="info-message warning">
                                        <i class="feather icon-alert-circle"></i>
                                        <p>선택한 조건에 맞는 스케줄이 없습니다.</p>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="info-message success">
                                        <i class="feather icon-check-circle"></i>
                                        <p>{{ previewCount }}건의 스케줄을 찾았습니다.</p>
                                        <div class="filter-summary">
                                            <p><strong>치료실:</strong> {{ selectedRoomName }}</p>
                                            <p><strong>날짜:</strong> {{ formatDate(localCriteria.date) }}</p>
                                            <p><strong>시간:</strong> {{ localCriteria.hour }}:00 - {{ localCriteria.hour }}:59</p>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </vs-popup>
    </div>
</template>

<script>
export default {
    name: 'ScheduleFilterModal',
    props: {
        active: {
            type: Boolean,
            default: false
        },
        treatmentRooms: {
            type: Array,
            default: () => []
        },
        dateList: {
            type: Array,
            default: () => []
        },
        currentData: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            localCriteria: {
                medicalCd: '',
                date: '',
                hour: ''
            },
            hourOptions: [
                '09', '10', '11', '12', '13', '14', '15', '16', '17', '18'
            ]
        };
    },
    computed: {
        isActive: {
            get() {
                return this.active;
            },
            set(val) {
                this.$emit('update:active', val);
            }
        },
        isFormValid() {
            return this.localCriteria.date !== '' && this.localCriteria.hour !== '';
        },
        previewCount() {
            if (!this.isFormValid || !this.currentData || this.currentData.length === 0) {
                return 0;
            }

            return this.computeFilteredCount();
        },
        selectedRoomName() {
            if (this.localCriteria.medicalCd === '') {
                return '전체';
            }
            const room = this.treatmentRooms.find(r => r.MEDICAL_CD === this.localCriteria.medicalCd);
            return room ? room.MEDICAL_NM : '전체';
        }
    },
    methods: {
        formatDate(dateString) {
            if (!dateString || dateString.length !== 8) return dateString;
            const year = dateString.substring(0, 4);
            const month = dateString.substring(4, 6);
            const day = dateString.substring(6, 8);
            return `${year}-${month}-${day}`;
        },

        onFilterChange() {
            // Trigger preview count update (computed property will auto-update)
        },

        computeFilteredCount() {
            const { medicalCd, date, hour } = this.localCriteria;

            let count = 0;

            for (let timeBlock of this.currentData) {
                const schedulesOnDate = timeBlock.schedules_by_day && timeBlock.schedules_by_day[date]
                    ? timeBlock.schedules_by_day[date]
                    : [];

                for (let schedule of schedulesOnDate) {
                    // Medical code filter (empty string means "전체" - show all)
                    if (medicalCd && schedule.MEDICAL_CODE !== medicalCd) continue;

                    // Time filter: If hour is 'ALL', skip time filtering
                    if (hour !== 'ALL') {
                        const hourStart = hour + '00';
                        const hourEnd = hour + '59';
                        const realTime = schedule.REAL_TIME_STRING;
                        if (!realTime || realTime < hourStart || realTime > hourEnd) continue;
                    }

                    count++;
                }
            }

            return count;
        },

        handleConfirm() {
            if (!this.isFormValid) return;

            this.$emit('confirm', { ...this.localCriteria });
            this.isActive = false;
        },

        handleCancel() {
            this.resetForm();
            this.$emit('cancel');
            this.isActive = false;
        },

        resetForm() {
            this.localCriteria = {
                medicalCd: '',
                date: '',
                hour: ''
            };
        }
    },
    watch: {
        active(newVal) {
            if (newVal) {
                // Reset form when modal opens
                this.resetForm();
            }
        }
    }
};
</script>

<style scoped>
/* Fullscreen modal styles */
::v-deep .vs-popup {
    width: 100vw !important;
    height: 100vh !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    border-radius: 0 !important;
}

::v-deep .vs-popup--content {
    width: 100% !important;
    height: calc(100vh - 60px) !important;
    max-height: calc(100vh - 60px) !important;
}

.filter-modal-container {
    width: 100%;
    height: 100%;
}

.filter-layout {
    display: flex;
    height: 100%;
    min-height: 500px;
}

/* Left Sidebar */
.filter-sidebar {
    width: 300px;
    padding: 20px;
    border-right: 1px solid #e0e0e0;
    background-color: #f8f9fa;
}

.filter-section {
    margin-bottom: 25px;
}

.filter-section-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
    color: #333;
}

.filter-buttons {
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
}

/* Right Content */
.filter-content {
    flex: 1;
    padding: 20px;
    background-color: #fff;
}

.result-preview {
    height: 100%;
}

.result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #e0e0e0;
}

.result-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
}

.result-count {
    display: flex;
    align-items: center;
    gap: 10px;
}

.count-label {
    font-size: 14px;
    color: #666;
}

.count-value {
    font-size: 24px;
    font-weight: 700;
    color: #439798;
}

.result-info {
    margin-top: 30px;
}

.info-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    text-align: center;
}

.info-message i {
    font-size: 48px;
    margin-bottom: 15px;
    color: #999;
}

.info-message.warning i {
    color: #f39c12;
}

.info-message.success i {
    color: #27ae60;
}

.info-message p {
    font-size: 16px;
    color: #666;
    margin: 0;
}

.filter-summary {
    margin-top: 20px;
    padding: 15px;
    background-color: #fff;
    border-radius: 6px;
    text-align: left;
    width: 100%;
    max-width: 400px;
}

.filter-summary p {
    margin: 8px 0;
    font-size: 14px;
    color: #333;
}

.filter-summary strong {
    color: #439798;
    margin-right: 8px;
}

/* Responsive */
@media (max-width: 768px) {
    .filter-layout {
        flex-direction: column;
    }

    .filter-sidebar {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid #e0e0e0;
    }

    .result-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
}
</style>
